import React from 'react';
import PropTypes from 'prop-types';
import { golf } from './enums';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { FormattedMessage } from 'react-intl';
import PopOver from '@ux/popover';
import Link from '@ux/link';
import Icon from '@ux/icon';

/**
 * Render A waffle menu to show a long list of products
 *
 * @param {Object} options Properties to spread on the item
 * @param {Function} [namespace=no-op] Function to namespace `className`
 * @returns {React.Element} A Popover Menu that uses a waffle control.
 * @api private
 */
class WaffleMenu extends NamespaceConsumer {

  /**
   * Generate a link
   * @param  {Object} opts enumeration of link content
   * @param  {String} opts.message title of link
   * @param  {String} opts.id golf id
   * @param  {String} opts.href link to product
   * @param  {String} opts.eid eid to track click
   * @param  {React.Element} opts.prefix Icon to place in front of the link
   * @returns {React.element} link to product
   */
  link({ message, id, href, eid, prefix, className }) {
    // TODO: respect urlArgs
    return (
      <Link
        title={ message }
        href={ href }
        data-eid={ eid }
        className={ className }
        key={ `waffle-menu-${id}` }>
        { prefix } <FormattedMessage id={ id } />
      </Link>
    );
  }

  links() {
    const { urls, messages, waffleVariant } = this.props;
    const eidPrefix = waffleVariant ?
      `uxp.hyd.waffle_menu_${waffleVariant}` :
      'uxp.hyd.waffle_menu';
    return {
      home: this.link({
        eid: `${eidPrefix}.home.link.click`,
        href: urls.products.href,
        id: golf.home,
        message: messages[golf.home],
        className: this.namespace('waffle-home-link'),
        prefix: <Icon name='home' />
      }),
      manage: this.link({
        eid: `${eidPrefix}.manage_my_products.link.click`,
        href: urls.products.href,
        id: golf['manage-my-products'],
        className: this.namespace('waffle-link'),
        message: messages[golf['manage-my-products']]
      }),
      myProducts: this.link({
        eid: `${eidPrefix}.my_products.link.click`,
        href: urls.products.href,
        id: golf.products,
        className: this.namespace('waffle-link'),
        message: messages[golf.products]
      }),
      shop: this.link({
        eid: `${eidPrefix}.shop_gd.link.click`,
        href: urls.home.href,
        id: golf.shopGD,
        className: this.namespace('waffle-link'),
        message: messages[golf.shopGD]
      }),
      productLinks: [{
        eid: `${eidPrefix}.product_domains.link.click`,
        href: urls.manageDomains.href,
        id: golf.domains,
        className: this.namespace('waffle-link'),
        message: messages[golf.domains]
      }, {
        eid: `${eidPrefix}.product_websites.link.click`,
        href: urls.manageWebsites.href,
        id: golf.websites,
        className: this.namespace('waffle-link'),
        message: messages[golf.websites]
      }, {
        eid: `${eidPrefix}.product_hosting_wordpress.link.click`,
        href: urls.manageHosting.href,
        id: golf['hosting-wordpress'],
        className: this.namespace('waffle-link'),
        message: messages[golf['hosting-wordpress']]
      }, {
        eid: `${eidPrefix}.product_email_office.link.click`,
        href: urls.bigthree.email.href,
        id: golf['email-office'],
        className: this.namespace('waffle-link'),
        message: messages[golf['email-office']]
      }, {
        eid: `${eidPrefix}.product_ssl.link.click`,
        href: urls.bigthree.ssl.href,
        id: golf.ssl,
        className: this.namespace('waffle-link'),
        message: messages[golf.ssl]
      }].map(this.link)
    };
  }

  render() {
    const { showWaffleMenu, control, waffleVariant } = this.props;

    if (!showWaffleMenu) {
      return null;
    }

    const links = this.links();
    const isControl = ['B', 'C', 'D'].indexOf(waffleVariant) === -1;

    return (
      <PopOver
        control={ control }
        eid={ `uxp.hyd.app_header.waffle_menu${waffleVariant ? '_waffleVariant' : ''}.click` }
        className={ this.namespace('waffle-menu') }
      >
        { isControl && links.home }
        { waffleVariant === 'B' && links.manage }
        { waffleVariant === 'C' && links.myProducts }
        { ['B', 'D'].indexOf(waffleVariant) !== -1 && links.shop }
        <div className={ this.namespace('tray-menu-divider') }></div>

        <div className={ this.namespace('quick-links-heading', 'tray-heading') }>
          <FormattedMessage id={ 'PC:AppHeader:Header:QuickLinks' } />
        </div>

        <div className={ this.namespace('quick-links') }>
          { links.productLinks }
        </div>

        {
          (isControl || waffleVariant === 'D') &&
          <div className={ this.namespace('tray-menu-divider') } />
        }

        { isControl && links.manage }
        { waffleVariant === 'D' && links.myProducts }
      </PopOver>
    );
  }
}

WaffleMenu.propTypes = {
  ...NamespaceShape,
  control: PropTypes.element,
  showWaffleMenu: PropTypes.bool,
  urls: PropTypes.object.isRequired,
  messages: PropTypes.object
};

export default withNamespace(WaffleMenu);
